import React, { useContext, useMemo } from 'react';
import { Button, Card, Alert } from 'react-bootstrap';
import { Column } from 'react-table';
import { useAsync } from 'react-commons';
import { deleteDelegate, getDelegates } from '../../api/delegateApi';
import { Delegate } from '../../api/delegateApiTypes';
import ConfigContext from '../../contexts/ConfigContext';
import Spinner from '../common/Spinner';
import { convertEmptyStringToDashes, renderDateCell, sendToast } from '../../utils/displayUtils';
import DeleteConfirmationModal from '../common/modal/DeleteConfirmationModal';
import { INITIAL_SELECT_ID } from '../../utils/Constants';
import DelegateModal from './modal/DelegateModal';
import { FormType } from '../common/formTypes';
import ErrorBlock from '../common/ErrorBlock';
import EmployeeIdContext from '../../contexts/EmployeeIdContext';
import DataGrid from '../common/datatables/DataGrid';

export interface DelegateRowType {
    original: {
        id: number;
        personId: number;
        proxyId: number;
        proxyName: string;
        description: string;
        startDate: string;
        endDate: string;
        active: boolean;
    };
}

const UserDelegate = () => {
    const config = useContext(ConfigContext);
    const employeeId = useContext(EmployeeIdContext);

    const defaultState: Array<Delegate> = [];
    const { value, error, loading, refresh } = useAsync<Array<Delegate>>(
        () => getDelegates(config),
        defaultState,
    );

    const empId = employeeId || INITIAL_SELECT_ID;

    const delegates = value.map((delegate) => ({
        id: delegate.id ? delegate.id : INITIAL_SELECT_ID,
        personId: delegate.personId ? delegate.personId : employeeId,
        proxyName: delegate.proxyName ? delegate.proxyName : '',
        proxyId: delegate.proxyId ? delegate.proxyId : INITIAL_SELECT_ID,
        description: delegate.description,
        startDate: delegate.startDate,
        endDate: delegate.endDate,
        active: delegate.active,
    }));

    const isDelegationsFull = value.length >= 5;

    if (error) {
        return typeof error === 'object' ? (
            <ErrorBlock errorMessage="Network Error!" />
        ) : (
            <ErrorBlock errorMessage={error} />
        );
    }

    const columns = [
        {
            Header: '',
            accessor: 'active',
            Cell: (cellProps: { value: boolean }) =>
                useMemo(
                    () => (
                        <div className="text-center">
                            {cellProps.value ? (
                                <i className="fas fa-check-circle text-success" />
                            ) : (
                                ''
                            )}
                        </div>
                    ),
                    [],
                ),
            disableSortBy: true,
            xs: 1,
            sm: 1,
            xl: 1,
            customClass: 'am-list-action',
        },
        {
            Header: 'Delegate',
            accessor: 'proxyName',
            xs: 12,
            sm: 12,
            xl: 2,
        },
        {
            Header: 'Description',
            accessor: 'description',
            Cell: (cellProps: { value: string }) =>
                useMemo(() => <span>{convertEmptyStringToDashes(cellProps.value)}</span>, []),
            xs: 12,
            sm: 12,
            xl: 3,
        },
        {
            Header: 'Start Date',
            accessor: 'startDate',
            Cell: renderDateCell,
            xs: 6,
            sm: 6,
            xl: 2,
        },
        {
            Header: 'End Date',
            accessor: 'endDate',
            Cell: renderDateCell,
            xs: 6,
            sm: 6,
            xl: 2,
        },
        {
            Header: '',
            accessor: 'editRow',
            Cell: (cellProps: { row: DelegateRowType }) =>
                useMemo(
                    () => (
                        <DelegateModal
                            title="Edit Delegate"
                            toggleButton={
                                <Button variant="link" className="text-dark">
                                    <i className="fas fa-edit text-dark" /> Edit
                                </Button>
                            }
                            onSubmit={refresh}
                            formType={FormType.UPDATE}
                            personId={cellProps.row.original.personId}
                            id={cellProps.row.original.id}
                            proxyId={cellProps.row.original.proxyId}
                            proxyName={cellProps.row.original.proxyName}
                            description={cellProps.row.original.description}
                            startDate={cellProps.row.original.startDate}
                            endDate={cellProps.row.original.endDate}
                            active={cellProps.row.original.active}
                        />
                    ),
                    [],
                ),
            disableSortBy: true,
            xs: 6,
            sm: 6,
            xl: 1,
        },
        {
            Header: '',
            accessor: 'id',
            Cell: (cellProps: {
                row: { original: { amId: number; id: number; proxyName: string } };
            }) =>
                useMemo(
                    () => (
                        <DeleteConfirmationModal
                            title="Delete Delegate"
                            onConfirm={async () => {
                                await deleteDelegate(config, cellProps.row.original.id);
                                sendToast(
                                    <>
                                        Deleted Delegate:{' '}
                                        <strong>{cellProps.row.original.proxyName}</strong>
                                    </>,
                                );
                                refresh();
                            }}
                            body="Please confirm that you would like to delete this delegate. "
                            toggleButton={
                                <Button variant="link" className="text-danger">
                                    <i className="far fa-trash-alt text-danger" /> Delete
                                </Button>
                            }
                            confirmButtonText="Confirm Delete"
                        />
                    ),
                    [],
                ),
            disableSortBy: true,
            xs: 6,
            sm: 6,
            xl: 1,
        },
    ];

    return loading ? (
        <Spinner />
    ) : (
        <div className="container-fluid mt-3">
            {isDelegationsFull && (
                <Alert variant="info">Only 5 delegations are allowed per user.</Alert>
            )}
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">My Delegates</h3>

                    <DelegateModal
                        title="Create Delegate"
                        toggleButton={
                            <Button variant="primary" disabled={isDelegationsFull}>
                                <i className="fas fa-plus" /> Create Delegate
                            </Button>
                        }
                        onSubmit={refresh}
                        formType={FormType.CREATE}
                        personId={empId}
                    />
                </div>
            </div>
            <Card>
                <Card.Body>
                    <DataGrid
                        columns={columns as Column<Object>[]}
                        data={delegates}
                        noGlobalFilter
                        initialState={{ pageSize: 5 }}
                    />
                </Card.Body>
            </Card>
        </div>
    );
};

export default UserDelegate;
